<template>
  <div>
    
    <el-button class="settings-toggle relative z-10 flex mb-3" @click="navActive=!navActive" size="mini">
      <ContextMenuHeaderIcon type="info" plain class="inline-block context-menu-header-hover text-xs"/>
      <span class="inline-block self-center">SETTINGS</span>
    </el-button>
    
    <div class="settings-wrap block md:flex md:space-x-6 relative">
      
    <div class="settings-menu-wrap self-baseline" :class="navActive ? 'active':''" v-show="navActive || !isMobile">
      <div class="absolute right-0 top-1 z-10 text-gray-500 md:hidden" @click="navActive=!navActive">
        <i class="uil uil-multiply text-2xl"></i>
      </div>
      <h5 class="text-xs p-3 md:hidden">COMPANY SETTINGS MENU</h5>
      <el-menu
        data-cy="setting-ops"
        active-text-color="#000000"
        class="h-full rounded-tl-lg rounded-bl-lg settings-menu"
        :default-active="activePath"
        :router="true"
        @select="onMenuSelect"
        >
        <el-menu-item index="/settings/account-details" v-if="permissions['permissionFullAccess']">
          <i class="uil uil-diary-alt text-xl pr-1"></i>
          <span>Account Details</span>
        </el-menu-item>
        <el-menu-item index="/settings/invoices" v-if="permissions['permissionFullAccess']">
          <i class="uil uil-file-alt text-xl pr-1"></i>
          <span>Invoices</span>
        </el-menu-item>
        <el-menu-item index="/settings/company" v-if="permissions['permissionFullAccess']">
          <i class="uil uil-sliders-v-alt text-xl pr-1"></i>
          <span>Company Details</span>
        </el-menu-item>
        <el-menu-item
          index="scheduling-rostering"
          v-if="hasSchedulingRostering"
          @click="goToSchedulingRostering"
          class="mb-0"
          data-cy="scheduling-rostering-tab"
        >
          <i class="uil uil-calendar text-xl pr-1"></i>
          <span>Scheduling & Rostering</span>
        </el-menu-item>
        <el-menu-item
          index="scheduling-rules"
          v-if="hasSchedulingRostering"
          @click="goToSchedulingRules"
          class="settings-sub-item"
          data-cy="scheduling-rules-tab"
        >
          <i class="uil uil-corner-down-right text-sm pr-1"></i>
          <span>Scheduling Rules</span>
        </el-menu-item>
        <!-- HERA-9073: "Shift Types" replaced "Shifts" here. The Shifts settings page still
             exists in Athena and is still reachable from Athena's own Company Settings
             sidebar — only this Hera entry point was withdrawn. Do not re-add it without
             checking the ticket. `index` is a sentinel, not a route: these items redirect
             cross-app via goToAthena, so a real /settings/... value would make el-menu's
             :router="true" also attempt a Hera route that does not exist. -->
        <el-menu-item
          index="shift-types"
          v-if="hasSchedulingRostering"
          @click="goToShiftTypes"
          class="settings-sub-item"
          data-cy="shift-types-tab"
        >
          <i class="uil uil-corner-down-right text-sm pr-1"></i>
          <span>Shift Types</span>
        </el-menu-item>
        <el-menu-item
          index="associate-requests"
          v-if="hasSchedulingRostering"
          @click="goToAssociateRequests"
          class="settings-sub-item"
          data-cy="associate-requests-tab"
        >
          <i class="uil uil-corner-down-right text-sm pr-1"></i>
          <span>Associate Requests</span>
        </el-menu-item>
        <el-menu-item index="/settings/users" v-if="permissions['permissionFullAccess']" data-cy="user-tab">
          <i class="uil uil-users-alt text-xl pr-1"></i>
          <span>Users</span>
        </el-menu-item>
        <el-menu-item v-show="permissions['permissionFullAccess'] && $route.name === 'SettingsUserDetail'" index="/settings/users/detail" class="settings-sub-item">
          <i class="uil uil-corner-down-right text-sm pr-1"></i>
          <span>User Details</span>
        </el-menu-item>
        <el-menu-item index="/settings/users/roles" v-if="permissions['permissionFullAccess']" class="settings-sub-item">
          <i class="uil uil-corner-down-right text-sm pr-1"></i>
          <span>Roles</span>
        </el-menu-item>
        <el-menu-item index="/settings/users/org-chart" v-if="permissions['permissionFullAccess']" class="settings-sub-item">
          <i class="uil uil-corner-down-right text-sm pr-1"></i>
          <span>Org Chart</span>
        </el-menu-item>
        <el-menu-item index="api-keys" v-if="permissions['permissionFullAccess']" class="settings-sub-item" @click="goToServiceUsers">
          <i class="uil uil-corner-down-right text-sm pr-1"></i>
          <span>API Keys</span>
        </el-menu-item>
        <el-menu-item index="/settings/hera-ai" v-if="permissions['permissionFullAccess'] && (featurePermissions['featureEnabledVehiclePhotoLogsHeraAi'] || featurePermissions['featureEnabledAmazonChromePlugin'] || featurePermissions['featureEnabledAIChat'])">
          <i class="uil uil-atom text-xl pr-1"></i>
          <span>Hera AI</span>
        </el-menu-item>
        <el-menu-item index="/settings/driver-feedback" v-if="permissions['permissionFullAccess']">
          <i class="uil uil-comment-alt-dots text-xl pr-1"></i>
          <span>Coaching</span>
        </el-menu-item>
        <el-menu-item index="/settings/messenger" v-if="permissions['permissionMessenger'] || permissions['permissionMessageTemplate']" data-cy="messenger-tab">
          <i class="uil uil-comment-alt-dots text-xl pr-1"></i>
          <span>Messenger</span>
        </el-menu-item>
        <el-menu-item index="/settings/drop-downs" v-if="permissions['permissionCustomLists']">
          <i class="uil uil-angle-down text-xl pr-1"></i>
          <span>Drop-Down Lists</span>
        </el-menu-item>
        <el-menu-item index="/settings/custom-lists" v-if="permissions['permissionCustomLists']" data-cy="custom-lists-tab">
          <i class="uil uil-list-ul text-xl pr-1"></i>
          <span>Custom Lists</span>
        </el-menu-item>
        <el-menu-item index="/settings/vehicle-photo-logs" v-if="permissions['permissionVehiclePhotoLogs'] || permissions['permissionFullAccess']" data-cy="vehicle-photo-logs">
          <i class="uil uil-image text-xl pr-1"></i>
          <span>Vehicle Photo Logs</span>
        </el-menu-item>
        <el-menu-item index="/settings/labels" v-if="(permissions['permissionManageLabels'] && (hasStaffManagement || hasVehicleManagement))">
          <i class="uil uil-label text-xl pr-1"></i>
          <span>Labels</span>
        </el-menu-item>
        <el-menu-item index="/settings/roster-checklists" v-if="permissions['permissionCompanySettingsRosterChecklistTemplates'] && featurePermissions['featureAccessRosterChecklist']" class="flex">
          <i class="uil uil-clipboard-notes text-xl"></i>
          <span>Roster Checklist</span>
        </el-menu-item>
        <el-menu-item
          index="phone-automation"
          v-if="isIvrEnabled && permissions['permissionFullAccess']"
          @click.native.capture.stop="goToPhoneAutomation"
          class="mb-0"
        >
          <i class="uil uil-phone-alt text-xl pr-1"></i>
          <span>Phone Automation</span>
        </el-menu-item>
        <el-menu-item
          index="phone-tree"
          v-if="isIvrEnabled && permissions['permissionFullAccess']"
          @click.native.capture.stop="goToPhoneAutomation"
          class="settings-sub-item"
        >
          <i class="uil uil-corner-down-right text-sm pr-1"></i>
          <span>Phone Tree</span>
        </el-menu-item>

        <el-menu-item index="/settings/inventory" v-if="featurePermissions['featureAccessInventoryManagement'] && permissions['permissionFullAccess']" class="flex">
          <i class="uil uil-cube text-xl"></i>
          <span>Inventory</span>
        </el-menu-item>
        <el-menu-item index="/settings/wireless-support-sync" v-if="featurePermissions['featureAccessWirelessSupportSync'] && permissions['permissionFullAccess']" class="flex">
          <i class="uil uil-mobile-android text-xl pr-1"></i>
          <span>Wireless Support Sync</span>
        </el-menu-item>
        <el-menu-item index="/settings/associate-app" class="flex" v-if="permissions['permissionFullAccess'] && featurePermissions['featureAccessAssociateApp']">
          <i class="uil uil-mobile-android text-xl"></i>
          <span>Associate App</span>
        </el-menu-item>
        <el-menu-item
          index="payroll-provider"
          v-if="showPayrollProviderMenu"
          @click.native.capture.stop="goToPayrollProvider"
          class="flex"
          data-cy="payroll-provider-tab"
        >
          <i class="uil uil-money-bill text-xl"></i>
          <span>Payroll Provider</span>
        </el-menu-item>
        <el-menu-item
          index="payroll-sync-results"
          v-if="showPayrollSyncMenu"
          @click.native.capture.stop="goToPayrollSyncResults"
          class="settings-sub-item"
          data-cy="payroll-sync-results-tab"
        >
          <i class="uil uil-corner-down-right text-sm pr-1"></i>
          <span>Associate Sync Results</span>
        </el-menu-item>
        <el-menu-item
          index="payroll-sync-history"
          v-if="showPayrollSyncMenu"
          @click.native.capture.stop="goToPayrollSyncHistory"
          class="settings-sub-item"
          data-cy="payroll-sync-history-tab"
        >
          <i class="uil uil-corner-down-right text-sm pr-1"></i>
          <span>Sync History</span>
        </el-menu-item>
      </el-menu>
    </div>

      <div class="flex-1 grid grid-cols-1">
        <transition name="fade" mode="out-in">   
            <router-view name='settings' :key="$route.path"/>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>

import { mapGetters, mapState } from 'vuex'
import ContextMenuHeaderIcon from '@/components/ContextMenuHeaderIcon';
import { getUserPermissions, getTenantFeaturePermissions } from '@/middlewares/permissions'
import { isPayrollProviderMenuVisible, isPayrollSyncMenuVisible } from '@/utilities/payrollProviderMenu'

export default {
  components:{ ContextMenuHeaderIcon },
  data() {
    return {
      permissions: {},
      windowWidth: window.innerWidth,
      navActive: false
    }
  },
  computed:{
    ...mapGetters([
      'hasStaffManagement',
      'hasVehicleManagement',
      'isAdmin'
    ]),
    activePath(){
      var path = this.$route.path
      var subPaths = ['/settings/users/roles', '/settings/users/org-chart', '/settings/users/detail']
      if (subPaths.some(function(p) { return path.startsWith(p) })) return path
      var pathArray = path.split("/")
      var basePath = `/${pathArray[1]}/${pathArray[2]}`
      return basePath
    },
    featurePermissions(){
      return getTenantFeaturePermissions()
    },
    hasSchedulingRostering(){
      return this.permissions['permissionCompanySettingsSchedulingRostering'] && this.featurePermissions['featureAccessSchedulingAndRostering'] && this.featurePermissions['featureEnabledSchedulingAndRostering']
    },

    isIvrEnabled(){
      return this.featurePermissions['featureAccessPhoneAutomation'] && this.featurePermissions['featureEnabledPhoneAutomation']
    },

    payrollTenantFeaturePermissions(){
      try {
        return getTenantFeaturePermissions()
      } catch (e) {
        return null
      }
    },
    showPayrollProviderMenu(){
      return isPayrollProviderMenuVisible(this.payrollTenantFeaturePermissions, this.$store.state.userInfo)
    },
    showPayrollSyncMenu(){
      return isPayrollSyncMenuVisible(this.payrollTenantFeaturePermissions, this.$store.state.userInfo)
    },

    isMobile(){
      return this.windowWidth <= 768
    }
  },

  methods: {
    onMenuSelect() {
      // Close mobile menu when item is selected
      if (this.isMobile) {
        this.navActive = false
      }
    },
    goToServiceUsers() {
      window.location.href = this.goToAthena('/settings/service-users')
    },
    goToSchedulingRostering() {
      window.location.href = this.goToAthena('/settings/scheduler/operating-hours')
    },
    goToSchedulingRules() {
      window.location.href = this.goToAthena('/settings/scheduler/scheduling-rules')
    },
    goToShiftTypes() {
      window.location.href = this.goToAthena('/settings/scheduler/shift-types')
    },
    goToAssociateRequests() {
      window.location.href = this.goToAthena('/settings/scheduler/associate-requests')
    },
    goToPayrollProvider() {
      window.location.href = this.goToAthena('/payroll-provider/settings')
    },
    goToPayrollSyncResults() {
      window.location.href = this.goToAthena('/payroll-provider/sync')
    },
    goToPayrollSyncHistory() {
      window.location.href = this.goToAthena('/payroll-provider/sync-history')
    },
    goToPhoneAutomation() {
      window.location.href = this.goToAthena('/ivr')
    },
    handleResize() {
      this.windowWidth = window.innerWidth
      // Close menu when switching to desktop
      if (!this.isMobile) {
        this.navActive = false
      }
    }
  },

  mounted() {
    window.addEventListener('resize', this.handleResize)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },

  beforeMount() {
    getUserPermissions(this.$store.state.userInfo.id, this.$store.state.userInfo.tenant.group)
    .then(userPermissions => {
      this.permissions = userPermissions
    })
  },
}

</script>

<style lang="scss" scoped>
  .router-link-active{
    @apply text-brand;
  }
  .el-menu-item.is-active{
    @apply bg-gray-200;
  }
</style>

<style>
/* HERA-9073: spacing matched to the Athena Company Settings sidebar, whose row primitive
   (athena src/modules/custom-list/components/CustomListMenuItem.vue) is
   `m-2 px-3 py-[7px] rounded-lg text-sm` with a `ml-6` indent wrapper for sub-items.
   These rules are deliberately unscoped because they reach Element UI internals, so they
   also style the User Settings menu (views/NotificationSettings/NotificationSettingsIndex.vue),
   which shares the .settings-menu hook and defines no styling of its own — check it when
   changing anything here.
   Keep h-auto and the explicit line-height: Element UI ships .el-menu-item at a fixed 56px
   height and line-height, and those are what let the row height follow its content + padding.
   The 7px vertical padding is plain CSS, not py-[7px], because this project runs Tailwind
   2.2 in AOT mode, which does not generate arbitrary values.

   HERA-9073 QA retest (UAT-07): padding alone did not reproduce Athena's row. Athena's row is
   a flex box whose height comes from its icon box (28px for top-level uil icons, 24px for the
   sub-item arrow container) plus 7px padding, i.e. 42px / 38px. Hera's rows used a 16px line
   box, so they came out shorter and the nested entries still looked bunched. The rules below
   pin the same boxes: flex + centered, 20px text line (Tailwind text-sm), 28px / 24px icon
   boxes, the 8px/4px icon inset applied to every icon (Athena puts pl-2 on all icons, not
   only the Scheduling & Rostering group), and the span's 3px top nudge.
   The panel no longer has a fixed w-48: Athena's sidebar is sized to its content, and
   Element UI rows are white-space: nowrap, so a fixed 192px made the longer labels
   ("Scheduling & Rostering", indented "Associate Requests") overflow the panel. */
.settings-menu .el-menu-item{
    @apply px-3 mx-2 rounded-lg h-auto my-2 align-middle !important;
    display: flex !important;
    align-items: center !important;
    line-height: 20px !important;
    padding-top: 7px !important;
    padding-bottom: 7px !important;
}
.settings-menu .el-menu-item > i{
    display: inline-block;
    line-height: 28px !important;
    padding-left: 8px !important;
    padding-right: 4px !important;
}
.settings-menu .el-menu-item > span{
    padding-top: 3px;
    padding-right: 8px;
}
/* HERA-9073: no my-0 here on purpose — sub-items inherit my-2 (8px) so they sit as far apart
   as Athena's do. Their zero vertical margin is what made the nested entries look cramped.
   Indent: Athena wraps sub-items in an ml-6 (24px) div AND each row keeps its own m-2 (8px),
   so a sub-item row starts 32px from the panel edge. Here the margin sits on the row itself
   and replaces mx-2's left 8px, so the equivalent is ml-8 (32px), not ml-6. */
.settings-menu .el-menu-item.settings-sub-item{
    @apply ml-8 text-sm !important;
} 
/* Athena's sub-item arrow sits in a 24px icon container, so sub-item rows are 38px tall. */
.settings-menu .el-menu-item.settings-sub-item > i{
    line-height: 24px !important;
} 
.settings-menu .el-menu-item.el-menu-item.is-active{
    @apply bg-gray-200 font-semibold !important;
} 
.settings-menu .el-menu-item:hover, .el-menu-item:focus {
	@apply bg-gray-200 text-black !important;
}
.settings-menu .el-menu-item:hover i{
	@apply  text-black !important;
}

.settings-toggle{
  display: none !important;
}

@media only screen and (max-width: 768px) {
  .settings-wrap h1{
    @apply relative;
  }
  .settings-toggle{
    display: block !important;
    color: #aaa;
    margin-bottom: 1em !important;
  }
  .settings-toggle > span{ 
    display: flex;
  }
  .settings-menu-wrap{
    position: absolute;
    transition: transform .2s ease, opacity .2s ease;
    top: -3.5em;
    height: auto;
    background: #fff;
    width: auto;
    transform: translate(-15rem, 0);
    opacity: 0;
    padding: 2em 2em 2em 4.5em;
    box-shadow: 1px 3px 10px rgb(0, 0, 0, .2);
    pointer-events: none;
    z-index: 20;
  }
  .settings-menu-wrap.active{
    transform: translate(-4.5rem, 0);
    opacity: 1;
    pointer-events: auto;
  }
  .settings-menu{
    border:none !important
  }
}
</style>
